import type { ApiResponse } from "../types";
import { AppError, BusinessError, NotFoundError } from "../errorTypes";
import axios from './../axios'
import type { AuthRepository } from '@trading/core/src/repositories/auth/auth.repository'
import type RegisterDTO from '@trading/core/src/repositories/auth/dto/register.dto'
import type LoginDTO from '@trading/core/src/repositories/auth/dto/login.dto'
import { isAxiosError } from 'axios'
import type { AuthPayload } from '@trading/core/src/models/auth.model'
import type { SsoDTO } from "@trading/core";

export class AuthRepositoryAxios implements AuthRepository {
  constructor(private apiClient = axios) {
  }

  login(dto: LoginDTO) {
    return this.apiClient
      .post<ApiResponse<AuthPayload>>('/auth/login', dto, {
        isAuthRequired: false,
        withCredentials: true,
        metadataDescription: {
          item: 'Auth',
          action: 'Login'
        }
      })
      .then((resp) => {
        if (resp instanceof AppError) {
          if (resp instanceof NotFoundError) {
            resp.message = 'Нет такого пользователя'
          }
          throw resp
        }

        const authData = resp.data
        if (authData.success) {
          return authData
        } else {
          throw authData.error
        }
      })
      .catch((error) => {
        throw error
      })
  }

  loginBySso(dto: SsoDTO) {
    return this.apiClient
      .post<ApiResponse<AuthPayload>>('/auth/login/token', dto, {
        isAuthRequired: false,
        withCredentials: true,
        metadataDescription: {
          item: 'Auth',
          action: 'Login'
        }
      })
      .then((resp) => {
        if (resp instanceof AppError) {
          if (resp instanceof NotFoundError) {
            resp.message = 'Нет такого пользователя'
          }
          throw resp
        }

        const authData = resp.data
        if (authData.success) {
          return authData
        } else {
          throw authData.error
        }
      })
      .catch((error) => {
        throw error
      })
  }

  loginByAccount(dto: LoginDTO) {
    return this.apiClient
      .post<ApiResponse<AuthPayload>>('/auth/login/account', dto, {
        isAuthRequired: false,
        withCredentials: true,
        metadataDescription: {
          item: 'Auth',
          action: 'Login'
        }
      })
      .then((resp) => {
        if (resp instanceof AppError) {
          if (resp instanceof NotFoundError) {
            resp.message = 'Нет такого пользователя'
          }
          throw resp
        }

        const authData = resp.data
        if (authData.success) {
          return authData
        } else {
          throw authData.error
        }
      })
      .catch((error) => {
        throw error
      })
  }

  register(dto: RegisterDTO) {
    return this.apiClient
      .post<ApiResponse<AuthPayload>>('/auth/register', dto, {
        isAuthRequired: false,
        metadataDescription: {
          item: 'Auth',
          action: 'Register'
        }
      })
      .then((resp) => {
        const authData = resp.data
        if (authData.success) {
          return authData
        } else {
          throw new Error('При регистрации пришёл не правильный ответ от сервера')
        }
      })
      .catch((error) => {
        if (isAxiosError(error)) {
          // TypeScript теперь знает, что error — это объект AxiosError можно
          // переделать запрос при 500 или 502 или выкинуть ошибку на основе статуса ответа
          console.log('Статус ответа:', error.response?.status);
          console.log('Данные от сервера:', error.response?.data);
          throw error.response?.data
        } else {
          // Неизвестная Ошибки сети или кода (например, TypeError)
          throw error
        }
      })
  }

  logout() {
    return this.apiClient.get<ApiResponse<void>>('/auth/logout', {
      isAuthRequired: false,
      metadataDescription: {
        item: 'Auth',
        action: 'Logout'
      }
    })
      .then((resp) => {
        if (resp instanceof AppError) {
          throw resp
        }

        const logoutData = resp.data
        if (logoutData.success) {
          return logoutData
        } else {
          throw new BusinessError('При логауте пришёл не правильный ответ от сервера')
        }
      })
      .catch((error) => {
        // if (isAxiosError(error)) {
        //   // TypeScript теперь знает, что error — это объект AxiosError можно
        //   // переделать запрос или выкинуть ошибку на основе статуса ответа
        // } else {
        //   // Неизвестная Ошибки сети или кода (например, TypeError)
        // }
        throw error
      })
  }

  refresh() {
    return this.apiClient
      .post(
        '/auth/refresh/cookie',
        {},
        {
          isRefreshRequest: true,
          isAuthRequired: false,
          withCredentials: true,
          xsrfCookieName: 'csrf_token',
          xsrfHeaderName: 'X-CSRF-Token',

          // Важно, когда фронт и API живут на разных поддоменах
          withXSRFToken: true,
          metadataDescription: {
            item: 'Auth',
            action: 'Refresh'
          }
        }
      )
      .then((resp) => resp.data)
  }
}
